import { action, makeObservable, observable, runInAction } from 'mobx';
import {
    buildWhereFilter,
    EntityName,
    get,
    getCount,
    getDetailsForCorrectPagination,
    getPage,
    post
} from 'stores/_services/dataService';
import dayjs from 'dayjs';
import { getOptions } from 'stores/_helpers/analysisHelpers';
import { getConditions } from 'stores/_services/filterService';
import TaskItemStore, { ITaskItem } from './TaskItemStore';
import { DEFAULT_LIMIT, DEFAULT_PAGE_NUMBER, MAX_DATA_LENGTH } from 'stores/BaseListStore';
import { IUser } from 'stores/user/UserStore';
import { IDataFetchOptions, IGetDataParams } from '../../types';
import { TaskStatus } from 'constants/TaskStatus';
import { isNumber } from 'lodash';

const SEARCH_FIELDS = ['name'];
const TASK_ITEM_SEARCH_FIELDS = ['name'];
const DEFAULT_TASK_ITEM_FILTER = {
    search: '',
    taskStatus: {}
};

interface ITaskStatus {
    order: number;
    count: number;
    name: string;
    status: TaskStatus;
}

interface ITaskItemCount {
    complete: number;
    failed: number;
    isComplete: boolean;
    progress: number;
    total: number;
}

export interface ITask {
    id: string;
    userId: null | string;
    createdOn: Date | null;
    startedOn: Date | null;
    endedOn: Date | null;
    message: string;
    name: string;
    data?: Record<string, any>;
    progress: number;
    itemCount: number;
    relatedId: string | null;
    jobId: string | null;
    type: string;
    status: TaskStatus;
    stats?: { status: Record<string, ITaskStatus>; itemCounts?: Record<string, ITaskItemCount> };
    steps: ({ type: string } & ITaskItemCount)[];
    archived: null;
    user: IUser | null;
    results: unknown;
    previousStatus?: string | null;
    statuses?: string[];
}

export default class TaskStore {
    limit = 25;
    skip = 0;
    sortBy = 'createdOn';
    sortDirection = 'DESC';
    filter: unknown = { ...DEFAULT_TASK_ITEM_FILTER };
    pageSize = DEFAULT_LIMIT;
    page = DEFAULT_PAGE_NUMBER;
    totalCount = 0;
    count = 0;
    taskItemIds: string[] = [];
    taskItemData = new Map();
    taskItemCount = 0;
    selectedTaskItemIds: Record<string, boolean> = {};
    task: ITask = {
        id: '',
        userId: null,
        createdOn: null,
        startedOn: null,
        endedOn: null,
        message: '',
        name: '',
        data: {},
        progress: 0,
        itemCount: 0,
        relatedId: null,
        jobId: null,
        status: TaskStatus.NONE,
        type: '',
        stats: { status: {} },
        steps: [],
        archived: null,
        user: null,
        results: [],
        previousStatus: null
    };

    constructor(current: ITask, previous: ITask) {
        makeObservable(this, {
            sortBy: observable,
            sortDirection: observable,
            filter: observable,
            taskItemIds: observable,
            taskItemData: observable,
            taskItemCount: observable,
            selectedTaskItemIds: observable,
            task: observable,
            update: action,
            archive: action,
            cancelTaskItem: action,
            cancel: action,
            cancelTaskItemsByQuery: action,
            getTaskItem: action,
            getTaskItems: action,
            resetTaskItemFilter: action,
            clearTaskItems: action,
            toggleTaskItem: action,
            deselectAllTaskItems: action,
            _updateTaskItemIds: action,
            _updateTaskItemData: action
        });

        this.task = previous ? previous : this.task;
        this._setTaskData(current);
    }

    get taskItems(): TaskItemStore[] {
        return this.taskItemIds.map((id) => this.taskItemData.get(id));
    }

    get elapsed(): number {
        const end = this.task.endedOn ? dayjs(this.task.endedOn) : dayjs();
        return dayjs(end).diff(this.task.startedOn as Date);
    }

    get selectedTaskItemIdList(): string[] {
        return Object.keys(this.selectedTaskItemIds).filter((key) => this.selectedTaskItemIds[key]);
    }

    findTaskItem(taskItemId: string): TaskItemStore {
        return this.taskItemData.get(taskItemId);
    }

    update(task: ITask): void {
        this._setTaskData(task);
    }

    async archive(): Promise<void> {
        await post(`userTasks/${this.task.id}/archive`);
    }

    async cancelTaskItem(taskItemStore: TaskItemStore): Promise<void> {
        taskItemStore.cancel();
        await this.getTaskItem(taskItemStore.taskItem.id);
    }

    async cancel(): Promise<void> {
        if (this.task.name.match(/refreshing/i)) {
            await post(`userTasks/${this.task.id}/cancel`, { boxId: this.task.relatedId });
        } else if (this.task.name.match(/analyzing/i)) {
            await post(`userTasks/${this.task.id}/cancel`, { filerepIds: this.task.relatedId });
        } else {
            await post(`userTasks/${this.task.id}/cancel`);
        }
    }

    async cancelTaskItemsByQuery(conditions: any[]): Promise<void> {
        conditions.push({ userTaskId: this.task.id });

        const where = buildWhereFilter({
            where: conditions,
            searchFields: SEARCH_FIELDS
        });

        await post('userTaskItems/cancel', where);

        this.getTaskItems();
    }

    async getTaskItem(taskItemId: string): Promise<TaskItemStore | undefined> {
        const { data } = await get(`userTaskItems/${taskItemId}`);

        this._updateTaskItemData(data);
        return this.taskItemData.get(taskItemId);
    }

    //TODO: move this out of TaskStore
    async getTaskItems(args: IDataFetchOptions = {}, pageNumber = DEFAULT_PAGE_NUMBER, pageSize = DEFAULT_LIMIT): Promise<void> {
        // @ts-ignore Need to move to TaskItemListStore
        const { skip, sort, sortBy, sortDirection, filter, shouldReset } = getOptions({
            state: {
                limit: this.limit,
                skip: this.skip,
                filter: this.filter,
                sortBy: this.sortBy,
                sortDirection: this.sortDirection
            },
            ...args
        });

        this.sortBy = sortBy;
        this.sortDirection = sortDirection;
        this.filter = filter;

        const conditions = getConditions(filter);
        conditions.push({ userTaskId: this.task.id });

        const options = {
            filter: {
                limit: pageSize,
                skip: (pageNumber - 1) * pageSize,
                searchFields: TASK_ITEM_SEARCH_FIELDS,
                includeFields: ['id', 'name', 'type', 'status', 'createdOn', 'endedOn'],
                where: conditions,
                order: sort
            }
        };

        const { data } = await getPage(EntityName.USER_TASKS_ITEMS, options);
        const totalCount = await getCount(EntityName.USER_TASKS_ITEMS, {
            where: conditions,
            searchFields: SEARCH_FIELDS
        });

        if (shouldReset) {
            //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
            runInAction(() => {
                this.taskItemIds = [];
                this.taskItemCount = 0;
            });
        }

        this._updateTaskItemData(data);
        this._updateTaskItemIds(data, skip);
        this._updateTaskItemCount(data);

        runInAction(() => {
            this.count = data.length;
            this.totalCount = totalCount;
            this.page = pageNumber;
            this.pageSize = pageSize;
        });
    }

    async getTaskItemsListForCorrectPagination(args: IGetDataParams = {}): Promise<void> {
        const { filter, sortBy, sortDirection, pageSize, page } = args;

        const params = {
            filter: { ...(filter || this.filter), userTaskId: this.task.id },
            sortBy: sortBy || this.sortBy,
            sortDirection: sortDirection || this.sortDirection,
            pageSize: pageSize || this.pageSize,
            page: page || this.page,
            searchFields: TASK_ITEM_SEARCH_FIELDS,
            includeFields: ['id', 'name', 'type', 'status', 'createdOn', 'endedOn']
        };

        const { data, totalCount } = await getDetailsForCorrectPagination<TaskStore>({
            params,
            entityName: EntityName.USER_TASKS_ITEMS
        });

        this._updateTaskItemData(data);
        this._updateTaskItemIds(data, 0);

        runInAction(() => {
            this.count = data.length;
            this.totalCount = totalCount;
            this.sortBy = sortBy || this.sortBy;
            this.sortDirection = sortDirection || this.sortDirection;
            this.filter = filter || this.filter;

            if (isNumber(page)) {
                this.page = page;
            }

            if (isNumber(pageSize)) {
                this.pageSize = pageSize;
            }
        });
    }

    resetTaskItemFilter(): void {
        this.filter = { ...DEFAULT_TASK_ITEM_FILTER };
    }

    clearTaskItems(): void {
        this.taskItemIds = [];
    }

    toggleTaskItem(taskItemId: string): void {
        this.selectedTaskItemIds[taskItemId] = !this.selectedTaskItemIds[taskItemId];
    }

    deselectAllTaskItems(): void {
        this.selectedTaskItemIds = {};
    }

    _updateTaskItemCount(data: ITask[]): void {
        if (data.length === this.limit) {
            runInAction(() => {
                this.taskItemCount += this.taskItemCount == 0 ? this.limit * 2 : this.limit;
            });
        } else {
            runInAction(() => {
                this.taskItemCount = this.taskItemCount < this.limit ? data.length : Math.max(0, this.taskItemCount - this.limit - data.length);
            });
        }
    }

    _updateTaskItemIds(data: ITask[], skip = 0): void {
        this.taskItemIds.length = Math.max(this.taskItemIds.length, data.length + skip);

        for (let i = 0; i < data.length; i++) {
            if (data[i]) {
                this.taskItemIds[i + skip] = data[i].id;
            }
        }
    }

    _updateTaskItemData(data: ITaskItem[]): void {
        if (!Array.isArray(data)) {
            data = [data];
        }

        for (const i in data) {
            const item = data[i];

            if (this.taskItemData.has(item.id)) {
                const oldItem = this.taskItemData.get(item.id);

                this.taskItemData.delete(item.id);
                oldItem.update(item);
                this.taskItemData.set(item.id, oldItem);
            } else {
                this.taskItemData.set(item.id, new TaskItemStore(item));

                if (this.taskItemData.size > MAX_DATA_LENGTH) {
                    this.taskItemData.delete(this.taskItemData.keys().next().value);
                }
            }
        }
    }

    _setTaskData(task: ITask): void {
        let statuses: string[] = [];
        let results: ITaskStatus[] = [];
        let steps: ({ type: string } & ITaskItemCount)[] = [];

        if (task && task.stats) {
            statuses = Object.keys(task.stats.status || {}).filter((status) => status !== 'total');

            if (task.stats.itemCounts) {
                const itemCounts = task.stats.itemCounts;

                steps = Object.keys(itemCounts)
                    .map((key) => {
                        return {
                            type: key,
                            ...itemCounts[key]
                        };
                    })
                    .sort((a, b) => (a.type < b.type ? -1 : 1));
            }

            if (task.stats.status) {
                results = Object.values(task.stats.status)
                    .filter((val) => 'order' in val)
                    .sort((a, b) => (a.order < b.order ? -1 : 1));
            }
        }

        this.task = {
            ...this.task,
            ...task,
            stats: task.stats || { status: {} },
            statuses,
            results,
            steps,
            previousStatus: this.task.status
        };
    }
}
